import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { functions, httpsCallable } from '@util/firebase';
import { AddressDocumentDraft } from '@util/types/firestore/address';
import { StripeAddressSchema } from '@util/types/firestore/stripe';
import { z } from 'zod';

export const createSellerIndividualSchema = z.object({
  address: StripeAddressSchema,
  dob: z
    .object({
      day: z.number(),
      month: z.number(),
      year: z.number(),
    })
    .optional(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  phone: z.string(),
  ssn_last_4: z.string().optional(),
  id_number: z.string().optional(),
  title: z.string().optional(),
  relationship: z
    .object({
      title: z.string(),
      owner: z.boolean(),
    })
    .optional(),
});

export const createSellerCompanySchema = z.object({
  address: StripeAddressSchema,
  name: z.string(),
  tax_id: z.string(),
  phone: z.string().optional(),
});

export const createSellerArgsSchema = z.object({
  business_type: z.string().optional().default('individual'),
  country: z.string().optional().default('US'),
  email: z.string(),
  individual: createSellerIndividualSchema.optional(),
  company: createSellerCompanySchema.optional()
});

export type CreateSellerArgs = z.infer<typeof createSellerArgsSchema>;

export const createSellerResponse = z.object({
  id: z.string(),
});

export type CreateSellerResponse = z.infer<typeof createSellerResponse>;

export function createSeller(args: CreateSellerArgs) {
  return httpsCallable<CreateSellerArgs, CreateSellerResponse>(
    functions,
    'stripe-createSellerAccount'
  )(args);
}

type GetPromoseArgs = {
  code: string;
  customer: string;
};

const couponSchema = z.object({
  id: z.string(),
  object: z.string(),
  amount_off: z.number().nullable(),
  created: z.number(),
  currency: z.null(),
  duration: z.string(),
  duration_in_months: z.number(),
  livemode: z.boolean(),
  max_redemptions: z.number(),
  metadata: z.object({
    teste: z.string(),
  }),
  name: z.string().optional(),
  percent_off: z.number(),
  redeem_by: z.null(),
  times_redeemed: z.number(),
  valid: z.boolean(),
});

const promotionCodeSchema = z.object({
  id: z.string(),
  object: z.string(),
  active: z.boolean(),
  code: z.string(),
  coupon: couponSchema,
  created: z.number(),
  customer: z.null(),
  expires_at: z.null(),
  livemode: z.boolean(),
  max_redemptions: z.null(),
  metadata: z.object({
    order_id: z.string(),
  }),
  restrictions: z.object({
    first_time_transaction: z.boolean(),
    minimum_amount: z.null(),
    minimum_amount_currency: z.null(),
  }),
  times_redeemed: z.number(),
});

const getPromoResponseSchema = z.object({
  data: z.array(promotionCodeSchema),
});

const couponWithCodeSchema = couponSchema.merge(z.object({ code: z.string() }));

export type GetPromoResponse = z.infer<typeof getPromoResponseSchema>;
export type PromotionCode = z.infer<typeof promotionCodeSchema>;
export type Coupon = z.infer<typeof couponWithCodeSchema>;

export async function getPromos(
  args: GetPromoseArgs
): Promise<PromotionCode[]> {
  const res = await httpsCallable<GetPromoseArgs, GetPromoResponse>(
    functions,
    'getPromos'
  )(args);
  return res.data.data;
}

// Helpers

/**
 * Build an in-flight address from a Stripe AddressElement change event.
 *
 * Returns `AddressDocumentDraft`, NOT a strictly-validated `AddressDocument`.
 *
 * Phone source:
 * - If `overridePhone` is provided (V3 checkout: phone collected by a
 *   separate input outside the AddressElement), use it.
 * - Otherwise, fall back to `e.value.phone ?? ''` — Stripe's collected
 *   phone, which is present when the AddressElement has `fields.phone:
 *   'always'` (the default for all non-V3 callers).
 *
 * The strict `addressDocumentSchema.parse(...)` MUST be called at the
 * boundary into order creation (e.g., `InformationSection.onSubmit`) before
 * the draft is treated as a final `AddressDocument`.
 */
export function stripeAddressToAddressDocument(
  e: StripeAddressElementChangeEvent,
  overridePhone?: string
): AddressDocumentDraft {
  const [firstName, ...lastName] = e.value.name.split(' ');
  return {
    name: firstName + ' ' + lastName.join(' '),
    phone: overridePhone ?? e.value.phone ?? '',
    city_locality: e.value.address.city,
    country_code: e.value.address.country,
    address_line1: e.value.address.line1,
    address_line2: e.value.address.line2 ?? '',
    state_province: e.value.address.state ?? '',
    postal_code: e.value.address.postal_code,
    is_default: false,
  };
}
